import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { snapOrFromTo, snapOrTo } from 'animations/snapTween';
import { audioPath } from 'constants/audioConstants';
import {
  BET_PREFIX,
  GAME_HAND_PHASE,
  SEAT_CARD_BAND_LIFT_LANDSCAPE,
  SEAT_CARD_BAND_LIFT_PORTRAIT,
  SEAT_CONTAINER_WIDTH,
  SHOWDOWN_CARD_OVERLAP,
  SHOWDOWN_CARD_SCALE_BOOST,
  SMALL_CARD_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { TableIdContext } from 'contexts/TableIdContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import gsap from 'gsap';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useOrientation } from 'hooks/useOrientation';
import useSound from 'hooks/useSound';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableSnap } from 'hooks/useTableSnap';
import { TableZ } from 'styles/zIndex';
import { isWinner as isCurrentPotWinner, shouldDimCard } from 'utils/functions';
import { shouldPlayTableSfx } from 'utils/tableSfx';

import { isFaceDownCard, isValidCard, mapToCard } from 'components/Card';
import SmallCard from 'components/Card/SmallCard';

import { faceDownCardOffset, faceDownCardScale } from './holeCardFan';

// Face-up cards (your hand): 75% size. The face-down shape and scale live in
// ./holeCardFan, shared with modes/seatEdgeInsets.
const SCALE_FACE_UP = 0.75;

type SeatCardsPropType = {
  cardPosition: CardPosition;
  position: SeatPosition;
  cards: string[];
  seatId: number;
  allowShowdownReveal?: boolean;
};

export enum CardPosition {
  Left = 'LEFT',
  Right = 'RIGHT',
  Center = 'CENTER'
}

/**
 * Player cards positioned relative to their seat container.
 * Cards are dealt from center (0,0 relative to seat) and animate
 * out to the left or right of the seat.
 */
export const SeatCards = ({
  position,
  cards,
  seatId,
  allowShowdownReveal = false
}: SeatCardsPropType) => {
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  // True while this table is snapping to the state the player arrived at, so
  // cards are PLACED rather than dealt / folded / flipped again. See
  // hooks/useTableSnap.
  const shouldSnap = useTableSnap();
  const game = useTableSelector((table) => table.game);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const { pots } = game;
  const handNum = +game.hand;
  const hasDealtCards = Number.isFinite(handNum) && handNum >= GAME_HAND_PHASE.DEAL_CARDS;

  const gameID = useContext(TableIdContext);
  const { preDeal } = useTableSelector((table) => table.gameState);
  // Read the snapshot synchronously: a post-paint state update lets the layout
  // effect deal/reveal cards once before discovering that this seat folded.
  const isCurrentUserFolded = String(game['p' + seatId + 'bet'] ?? '').startsWith(BET_PREFIX.FOLD);
  const containerRef = useRef<HTMLDivElement>(null);

  const [playSpritDealingCards] = useSound(audioPath.dealingCards, {
    sprite: {
      dealingCards: [0 * 1000, 0.5 * 1000]
    }
  });

  // Unity plays DEALINGCARDS from each player's own UIPlayer with
  // isOwnSound = IsPOVPlayer() (UIPlayer.cs:910), so with opponents muted only
  // the POV player's own deal is audible. This fires per seat, so gate it the
  // same way rather than letting nine seats' deals through.
  // Optional-chained: seat components are rendered against partial table state
  // in places (and in tests), and an audio gate must never be able to crash the
  // felt. An absent seat reads as "unknown", which the gate treats as not-me.
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer?.seatId);
  const { muteOtherPlayers, muteOtherPlayersSetByUser } = useContext(TableSettingsContext);
  const dealSoundEnabled = shouldPlayTableSfx({
    origin: 'player',
    actorSeatId: seatId,
    currentPlayerSeatId,
    muteOtherPlayersSounds: muteOtherPlayers,
    muteOtherPlayersIsExplicit: muteOtherPlayersSetByUser
  });

  const playSoundDealingCard = useCallback(
    (id: string) => {
      if (!dealSoundEnabled) return;
      playSpritDealingCards({ id });
    },
    [playSpritDealingCards, dealSoundEnabled]
  );

  // Visitor snapshots can advertise facedown placeholders while a lone player
  // waits. Visibility/card payloads alone are not proof a hand has been dealt.
  const filteredCards = hasDealtCards
    ? cards.filter((card) => isValidCard(card) || isFaceDownCard(card))
    : [];

  const isLeftSide = position?.isLeft ?? false;
  const hasAnimatedRef = useRef(false);
  const dismissedRef = useRef(false); // Cards folded/swept — don't show again until next hand
  const prevPreDealRef = useRef(preDeal);
  const prevCardsRef = useRef<string[]>([]);

  // Detect showdown flip: cards change from facedown to real values — only for winners
  const cardKeys = filteredCards.join(',');
  const hadFaceDown = prevCardsRef.current.some((c) => isFaceDownCard(c));
  const nowFaceUp = filteredCards.length > 0 && filteredCards.every((c) => !isFaceDownCard(c));
  const isWinner = isCurrentPotWinner(pots, game.showdown, seatId);
  const isShowdownFlip =
    hadFaceDown && nowFaceUp && hasAnimatedRef.current && (isWinner || allowShowdownReveal);

  useEffect(() => {
    prevCardsRef.current = [...filteredCards];
  }, [cardKeys]);

  const faceDownScale = faceDownCardScale(isPortrait);
  // Revealed hands shrink only as far as fitting the seat's width demands — a
  // 2-card Hold'em hand stays at the design scale, a 4-card Omaha hand doesn't.
  const showdownScale = getShowdownCardScale(filteredCards.length);

  // Kill any in-flight card tweens (deal / fold / sweep / showdown flip) on
  // unmount. The animation effect below kills prior tweens on its *next* run,
  // but a seat that unmounts mid-animation (table close / view switch) would
  // otherwise orphan those tweens against detached card nodes — a leak source.
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (container) gsap.killTweensOf(container.querySelectorAll('.seat-card'));
    };
  }, []);

  // Layout effect, not a plain effect: this positions freshly-mounted cards
  // (e.g. an opponent's first hand, seen live the moment a second player joins
  // a table that had only one seated player) via inline transforms. A plain
  // effect runs after the browser paints, so that first mount would flash the
  // card at its unstyled default — full size, opacity 1, sitting at the seat's
  // anchor point — for one frame before this ever runs. PovCards (POV's own
  // cards) hit the same class of bug and fixed it the same way — see the
  // "Avoid a one-frame flash" comment there.
  useLayoutEffect(() => {
    if (!hasDealtCards) {
      hasAnimatedRef.current = false;
      dismissedRef.current = false;
      dealtHandBySeat.delete(`${gameID}:${seatId}`);
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    const cardEls = container.querySelectorAll('.seat-card');
    if (cardEls.length === 0) return;

    gsap.killTweensOf(cardEls);

    if (isHandHistorySnapshot) {
      dismissedRef.current = false;
      hasAnimatedRef.current = true;
      cardEls.forEach((el, index) => {
        const cardStr = filteredCards[index];
        const faceDown = cardStr ? isFaceDownCard(cardStr) : true;
        const cardScale = faceDown ? faceDownScale : showdownScale;
        const target = getCardOffset(
          index,
          faceDown,
          isLeftSide,
          position?.isCenter ?? false,
          isPortrait,
          filteredCards.length
        );

        gsap.set(el, {
          x: target.x,
          y: target.y,
          opacity: 1,
          rotation: target.rotation ?? 0,
          scale: cardScale,
          scaleX: 1
        });
      });
      prevPreDealRef.current = preDeal;
      return;
    }

    // Arrival snapshot: every branch below still decides WHERE the cards end
    // up, it just puts them there in one frame instead of animating.
    const snap = shouldSnap();

    const getTableCenter = () => {
      const tableRoot = container.closest('[data-table-root]');
      const tableRect = tableRoot?.getBoundingClientRect();
      return {
        x: tableRect ? tableRect.left + tableRect.width / 2 : window.innerWidth / 2,
        y: tableRect ? tableRect.top + tableRect.height / 2 : window.innerHeight / 2
      };
    };

    if (isCurrentUserFolded) {
      // Fold: cards spin and fly away — mark dismissed so they stay hidden
      hasAnimatedRef.current = false;
      dismissedRef.current = true;
      const tableCenter = getTableCenter();

      cardEls.forEach((el, index) => {
        const cardRect = el.getBoundingClientRect();
        const cardCenter = {
          x: cardRect.left + cardRect.width / 2,
          y: cardRect.top + cardRect.height / 2
        };
        const currentX = Number(gsap.getProperty(el, 'x')) || 0;
        const currentY = Number(gsap.getProperty(el, 'y')) || 0;

        snapOrTo(snap, el, {
          x: currentX + tableCenter.x - cardCenter.x,
          y: currentY + tableCenter.y - cardCenter.y,
          opacity: 0,
          rotation: isLeftSide ? -260 : 260,
          scale: 0.35,
          duration: 0.45,
          delay: index * 0.05,
          ease: 'power2.in'
        });
      });
    } else if (preDeal) {
      // End of hand: collect cards to center with a sweep
      hasAnimatedRef.current = false;
      dismissedRef.current = true;
      const tableCenter = getTableCenter();

      cardEls.forEach((el, index) => {
        const cardRect = el.getBoundingClientRect();
        const cardCenter = {
          x: cardRect.left + cardRect.width / 2,
          y: cardRect.top + cardRect.height / 2
        };
        const currentX = Number(gsap.getProperty(el, 'x')) || 0;
        const currentY = Number(gsap.getProperty(el, 'y')) || 0;

        snapOrTo(snap, el, {
          x: currentX + tableCenter.x - cardCenter.x,
          y: currentY + tableCenter.y - cardCenter.y,
          opacity: 0,
          rotation: (index % 2 === 0 ? -1 : 1) * 360,
          scale: 0.4,
          duration: 0.5,
          delay: 0.2 + index * 0.06,
          ease: 'power3.in'
        });
      });
    } else if (!hasAnimatedRef.current && handNum < GAME_HAND_PHASE.FIND_WINNERS) {
      // First deal: cards fly in from table center with arc.
      // This must run after preDeal/fold dismissal so the next hand can show cards again.
      hasAnimatedRef.current = true;
      dismissedRef.current = false;
      // Already dealt for THIS hand, and we are only here because the seat
      // remounted (an orientation change swaps the portrait and landscape
      // components). Place the cards rather than dealing them again — see
      // dealtHandBySeat for what replaying them costs.
      const dealKey = `${gameID}:${seatId}`;
      const placeOnly = snap || dealtHandBySeat.get(dealKey) === handNum;
      dealtHandBySeat.set(dealKey, handNum);
      // The deal cue belongs to the moment cards are dealt. Switching into a
      // table mid-hand is not that moment — the cards are already out.
      if (!placeOnly) playSoundDealingCard('dealingCards');

      const tableCenter = getTableCenter();

      cardEls.forEach((el, index) => {
        const cardStr = filteredCards[index];
        const faceDown = cardStr ? isFaceDownCard(cardStr) : true;
        const cardScale = faceDown ? faceDownScale : showdownScale;
        const target = getCardOffset(
          index,
          faceDown,
          isLeftSide,
          position?.isCenter ?? false,
          isPortrait,
          filteredCards.length
        );

        gsap.set(el, { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 });
        const cardRect = el.getBoundingClientRect();
        const cardCenter = {
          x: cardRect.left + cardRect.width / 2,
          y: cardRect.top + cardRect.height / 2
        };
        const startX = tableCenter.x - cardCenter.x;
        const startY = tableCenter.y - cardCenter.y;
        const startRotation = isLeftSide ? 25 : -25;

        snapOrFromTo(
          placeOnly,
          el,
          { x: startX, y: startY, opacity: 0, rotation: startRotation, scale: 0.2 },
          {
            x: target.x,
            y: target.y,
            opacity: 1,
            rotation: target.rotation ?? 0,
            scale: cardScale,
            duration: 0.45,
            delay: index * 0.15,
            ease: 'back.out(1.2)'
          }
        );
      });
    } else if (dismissedRef.current) {
      // Cards were folded/swept — keep them hidden until the next deal.
      return;
    } else if (isShowdownFlip) {
      // Showdown: cards flip from facedown to face-up and spread apart.
      //
      // Every other branch here states an OPACITY — 0 for fold and sweep, 1 for
      // the deal and for plain re-positioning. This one used to state none, and
      // so inherited whatever the last tween left behind.
      //
      // That is a real state after an orientation change: the seat remounts,
      // which resets hasAnimatedRef and replays the deal, and the deal fades
      // cards in on a per-card delay (index * 0.15). The next effect run kills
      // those tweens (killTweensOf above) and freezes the later cards at
      // opacity 0 — so a 4-card Omaha hand showed only its first card at
      // showdown. Reported 2026-08-26 on top-positioned seats, which are the
      // ones whose leftPct crosses the isLeft threshold as the container
      // re-measures, giving the extra effect run that does the killing.
      cardEls.forEach((el, index) => {
        const cardStr = filteredCards[index];
        const faceDown = cardStr ? isFaceDownCard(cardStr) : true;
        const cardScale = faceDown ? faceDownScale : showdownScale;
        const target = getCardOffset(
          index,
          faceDown,
          isLeftSide,
          position?.isCenter ?? false,
          isPortrait,
          filteredCards.length
        );

        // Flip animation: scale X to 0 (card edge), then back to full.
        // On arrival the reveal already happened — land on the revealed state.
        if (snap) {
          gsap.set(el, {
            x: target.x,
            y: target.y,
            opacity: 1,
            scale: cardScale,
            scaleX: 1,
            rotation: target.rotation ?? 0
          });
          return;
        }
        // Before the flip, not inside it: the card has to be visible for the
        // whole animation, and the timeline's per-card delay would otherwise
        // leave the last card invisible for another 0.3s.
        gsap.set(el, { opacity: 1 });
        const tl = gsap.timeline({ delay: index * 0.1 });
        tl.to(el, {
          scaleX: 0,
          duration: 0.15,
          ease: 'power2.in'
        }).to(el, {
          x: target.x,
          y: target.y,
          scale: cardScale,
          scaleX: 1,
          rotation: target.rotation ?? 0,
          duration: 0.3,
          ease: 'back.out(1.3)'
        });
      });
    } else {
      // Already dealt — maintain correct position and scale
      // Non-winners: keep face-down positioning even if engine reveals their cards
      const forceFaceDown = hadFaceDown && nowFaceUp && !isWinner && !allowShowdownReveal;
      cardEls.forEach((el, index) => {
        const cardStr = filteredCards[index];
        const faceDown = forceFaceDown || (cardStr ? isFaceDownCard(cardStr) : true);
        const cardScale = faceDown ? faceDownScale : showdownScale;
        const target = getCardOffset(
          index,
          faceDown,
          isLeftSide,
          position?.isCenter ?? false,
          isPortrait,
          filteredCards.length
        );

        gsap.set(el, {
          x: target.x,
          y: target.y,
          opacity: 1,
          rotation: target.rotation ?? 0,
          scale: cardScale
        });
      });
    }

    prevPreDealRef.current = preDeal;
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preDeal,
    isCurrentUserFolded,
    isLeftSide,
    isHandHistorySnapshot,
    cardKeys,
    faceDownScale,
    showdownScale,
    allowShowdownReveal,
    handNum,
    hasDealtCards,
    // getCardOffset takes isPortrait, so the placement is wrong without it.
    // It re-ran anyway only because faceDownCardScale returns a different
    // number per orientation — a coincidence standing in for a dependency.
    isPortrait
  ]);

  return (
    <div ref={containerRef} style={containerStyle}>
      {filteredCards.map((card: string, index: number) => {
        const isFaceDown = isFaceDownCard(card);
        const isDimmed = shouldDimCard(game, cards[index]);
        const pokerCard = mapToCard(cards[index]) ?? {};

        return (
          <div
            key={index}
            className='seat-card'
            style={{
              ...cardStyle,
              ...((isCurrentUserFolded || isDimmed) && darkenCard)
            }}>
            <SmallCard elevation={4} {...pokerCard} isFaceDown={isFaceDown} />
          </div>
        );
      })}
    </div>
  );
};

// Anchor the card layer on the seat's horizontal CENTER (which lines up with
// the avatar — see getAvatarPopStyle `left: 50%`); getCardOffset then fans the
// cards out to the seat's LEFT from there. The layer is deliberately ZERO-SIZE
// and absolute: cards can never contribute to the seat card's box, so flipping
// them face up at showdown cannot resize or move the seat.
const containerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '50%',
  width: 0,
  height: 0,
  // Above the seat's avatar and its dealer marker — see styles/zIndex.ts.
  zIndex: TableZ.withinSeatCards
};

/**
 * Which hand each seat has already dealt its cards for.
 *
 * MODULE level on purpose: it has to survive the component, because portrait and
 * landscape are different components (Seat/index.tsx) and rotating the device
 * therefore UNMOUNTS one and MOUNTS the other. That reset `hasAnimatedRef`, so a
 * hand already sitting on the felt was dealt again from scratch.
 *
 * Replaying it is not merely redundant, it is how cards went missing: the deal
 * fades each card in on a per-card delay (index * 0.15) from opacity 0, scale
 * 0.2, rotation ±25 at the table centre. Any re-run of the effect below calls
 * killTweensOf, and every card whose delay had not elapsed stays frozen exactly
 * there — invisible and in the wrong place. Reported 2026-08-26 and confirmed in
 * devtools: card 0 at its correct final transform, cards 1-3 all still at
 * `rotate(-25deg) scale(0.2)` with opacity 0.
 *
 * Keyed by table AND seat so one table's rotation cannot silence another's deal
 * in the multi-table grid.
 */
const dealtHandBySeat = new Map<string, number>();

/** Test seam: forget which hands have already been dealt. */
export const __resetDealtHands = (): void => dealtHandBySeat.clear();

const cardStyle: CSSProperties = {
  position: 'absolute',
  // A newly mounted waiting/folded seat may enter a delayed cleanup tween.
  // It has no positioned cards to collect. Stay hidden until a deal/reveal
  // explicitly places the cards and makes them visible (EN-10723).
  opacity: 0,
  zIndex: TableZ.withinSeatCards,
  transformOrigin: 'top left'
};

/**
 * Scale a REVEALED hand renders at, so the whole spread fits inside the seat
 * card's fixed width with every rank and suit still readable.
 *
 * Cards overlap by at most SHOWDOWN_CARD_OVERLAP (the rank glyph is centred on
 * the card, so overlapping further starts eating it), which makes the spread
 * `width * (1 + (n - 1) * (1 - overlap))` wide. A 2-card Hold'em hand already
 * fits at the design scale and is left untouched; a 4-card Omaha hand does not,
 * so it shrinks — that is what makes all four ranks visible instead of three of
 * them clipped.
 *
 * SHOWDOWN_CARD_SCALE_BOOST is then added, because the pure fit reads too small
 * on a phone. It is added BEFORE the SCALE_FACE_UP cap, so it can only ever
 * enlarge a hand that was shrunk to fit — a 2-card hand stays at the design
 * scale. See the constant for the footprint it spends.
 *
 * Exported for unit testing.
 */
export const getShowdownCardScale = (cardCount: number): number => {
  const spreadFactor = 1 + Math.max(0, cardCount - 1) * (1 - SHOWDOWN_CARD_OVERLAP);
  const fitScale = SEAT_CONTAINER_WIDTH / (SMALL_CARD_WIDTH * spreadFactor);
  return Math.min(SCALE_FACE_UP, fitScale + SHOWDOWN_CARD_SCALE_BOOST);
};

/**
 * Compute a card's X/Y offset relative to the AVATAR CENTER (the card layer is
 * anchored at the seat's horizontal centre — see containerStyle `left: 50%`).
 *
 * Cards use `transformOrigin: top left`, so `- scaledWidth / 2` shifts a card's
 * left edge half a card left, landing its centre on the avatar centre.
 *
 * EN-10520 changed two things here:
 *
 *  - **Face-down cards always fan LEFT**, on every seat. They used to follow the
 *    seat's outer side, which pushed the right-hand column's cards toward the
 *    viewport edge (clipped at 390px). One side everywhere also reads better:
 *    every avatar holds their cards in the same hand.
 *  - **Face-up cards shrink to fit the seat's width** and share the face-down
 *    cards' vertical band, so a revealed hand stays inside the footprint the
 *    seat already occupied mid-hand instead of growing over its neighbours —
 *    and lands ON the player's portrait rather than under it.
 *
 * Exported for unit testing the anchoring / fit invariants.
 */
export const getCardOffset = (
  index: number,
  isFaceDown: boolean,
  // Retained for call-site positional compatibility. Cards anchor left on every
  // seat now, so the seat's side no longer feeds the horizontal maths.
  _isLeftSide: boolean,
  _isCenter: boolean,
  isPortrait: boolean,
  cardCount: number
) => {
  const center = (cardCount - 1) / 2; // centering factor: 0.5 for 2 cards, 1.5 for 4 cards
  const scale = getShowdownCardScale(cardCount);
  const scaledWidth = SMALL_CARD_WIDTH * scale;
  const scaledHeight = SMALL_CARD_HEIGHT * scale;
  // ONE vertical anchor for both states — see SEAT_CARD_BAND_LIFT_*. Portrait
  // sits the band a touch higher over the avatar than landscape.
  const y = -(
    scaledHeight * (isPortrait ? SEAT_CARD_BAND_LIFT_PORTRAIT : SEAT_CARD_BAND_LIFT_LANDSCAPE)
  );

  if (isFaceDown) return faceDownCardOffset(index, cardCount, isPortrait);

  // Face-up (showdown): a fitted spread centred horizontally on the avatar, on
  // the SAME vertical band the face-down cards occupied — so the hand lands over
  // the player's portrait (covering the avatar is fine; hiding the cards behind
  // it is not) and the band does not jump when the cards flip.
  const gap = scaledWidth * (1 - SHOWDOWN_CARD_OVERLAP);
  return {
    x: (index - center) * gap - scaledWidth / 2,
    y,
    rotation: 0
  };
};

/** Hand sizes the felt deals — Hold'em and Omaha. */
const SHOWDOWN_HAND_SIZES = [2, 4];

/**
 * How far the TALLEST hand paints ABOVE the seat card's top edge, in unscaled px.
 *
 * Face-down AND face-up, both hand sizes, because a REVEAL reaches higher than a
 * concealed hand: a two-card showdown paints at SCALE_FACE_UP (0.75) where a
 * face-down card paints at 0.5 portrait / 0.6 landscape, and the band lift is a
 * fraction of that scaled height — so the band's top rises ~10px on the flip.
 *
 * Anything stacking above the cards has to clear THIS, not the face-down band
 * and not the avatar's painted reach. Modelling the face-down band alone is how
 * the uncalled-bet banner (2026-09-04) and the action bubble (2026-09-17) both
 * came to sit inside a revealed hand.
 *
 * Derived from `getCardOffset` rather than restated from the band-lift
 * constants, so it cannot drift from where the cards are actually drawn.
 */
export const seatCardBandReach = (isPortrait: boolean): number => {
  let highest = 0;
  for (const isFaceDown of [true, false]) {
    for (const cardCount of SHOWDOWN_HAND_SIZES) {
      // `y` is the card's TOP edge relative to the seat card's top, negative up.
      const { y } = getCardOffset(0, isFaceDown, false, false, isPortrait, cardCount);
      highest = Math.max(highest, -y);
    }
  }
  return highest;
};

const darkenCard: CSSProperties = {
  filter: 'brightness(0.4)'
};
